import { Component, OnInit, OnDestroy } from '@angular/core';
import { DynamicFilterSource, IFlexTableConfig, LoggerService, ToastService, regexSearch } from '@wephone-utils';
import { OutcallCampaignEntity } from '@wephone-core/model/entity/outcallcampaign';
import { ConfigManager, EntityManager } from '@wephone-core/wephone-core.module';
import { OutcallCampaignRepository } from '@wephone-core/model/repository/outcallcampaign';
import { CampaignActiveInfo, OutcallService } from '@wephone-core/service/outcall.service';
import { CallQueueEntity } from '@wephone-core/model/entity/callqueue';
import { CallQueueRepository } from '@wephone-core/model/repository/callqueue';
import * as _ from 'lodash';
import { _ti } from '@wephone-translation';
import { FAIcons } from '@wephone-custom-icon';
import { BehaviorSubject, Observable } from 'rxjs';
import { MyUserProfile } from '@wephone-core/service/config_manager.i';
import { AuthenticationService } from '@wephone-core/service/authentication';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { UserRepository } from '@wephone-core/model/repository/user';
import { Router } from '@angular/router';
import { FlexBasePage } from '@wephone-core-ui';

interface CampaignActiveInfoDisplayed extends CampaignActiveInfo {
  campaign: OutcallCampaignEntity;
  queue: CallQueueEntity;
  processed_percent: number;
}

interface GroupActiveInfoDisplayed extends CampaignActiveInfo {
  groupCampaigns?: CampaignActiveInfoDisplayed[];
  processed_percent: number;
  is_group: boolean;
}

@Component({
  selector: 'page-active-outbound-campaigns',
  templateUrl: './active-outbound-campaigns.component.html',
  styleUrls: ['./active-outbound-campaigns.component.scss']
})
export class ActiveOutboundCampaignsComponent extends FlexBasePage implements OnInit, OnDestroy {
  FAIcons = FAIcons;

  orderDir = false;
  orderBy: string;
  private activeCampaigns: CampaignActiveInfoDisplayed[] = []; // All campaigns
  updateActiveCamapignInterval: any;
  campaigns: { [keys: number]: OutcallCampaignEntity } = {};
  search: string;
  agentSort = {
    sort_column: 'name',
    sort_dir: 1
  };

  tabSelectedIndex = 0;

  // Table view
  view: 'group' | 'table' = 'table';
  dataSourceTable: DynamicFilterSource;
  tableConfigCampaign: IFlexTableConfig;

  dataSourceGroup: DynamicFilterSource;
  tableConfigGroup: IFlexTableConfig;

  filteredUserIds: number[];
  filteredGroupIds: number[];

  // Filters
  filters = {
    remainingCalls: false,
    callsToday: false,
    noAssignedAgent: false,
    noConnectedAgent: false,
    selectedGroups: [],
    selectedAgents: [],
    groupByGroup: false
  };

  // isReady: boolean = false;

  private readonly _dataSourceCampaign: BehaviorSubject<CampaignActiveInfoDisplayed[]>;
  private readonly _dataSourceGroup: BehaviorSubject<GroupActiveInfoDisplayed[]>;

  private outcallRepo: OutcallCampaignRepository;

  constructor(
    private readonly router: Router,
    private readonly em: EntityManager,
    private readonly outcallService: OutcallService,
    // private readonly loggerService: LoggerService,
    private readonly configManager: ConfigManager,
    private readonly authService: AuthenticationService,
    protected readonly toast: ToastService
  ) {
    super();

    this.outcallRepo = this.em.getRepository('OutcallCampaignRepository') as OutcallCampaignRepository;
    this._dataSourceCampaign = new BehaviorSubject<CampaignActiveInfoDisplayed[]>([]);
    this._dataSourceGroup = new BehaviorSubject<GroupActiveInfoDisplayed[]>([]);
  }

  private getDataSourceCampaign<T extends CampaignActiveInfoDisplayed = CampaignActiveInfoDisplayed>(): Observable<
    T[]
  > {
    return this._dataSourceCampaign.asObservable() as Observable<T[]>;
  }

  private getDataSourceGroup<T extends GroupActiveInfoDisplayed = GroupActiveInfoDisplayed>(): Observable<T[]> {
    return this._dataSourceGroup.asObservable() as Observable<T[]>;
  }

  // async ngOnInit(): Promise<void> {
  //   super.ngOnInit();
  // }

  protected async resolveData(): Promise<void> {
    this.orderBy = 'name';
    const campaigns: OutcallCampaignEntity[] = this.outcallRepo.getObjectList();
    for (const campaign of campaigns) {
      this.campaigns[campaign.getId()] = campaign;
    }

    if (!this.authService.isAdmin()) {
      const myProfile: MyUserProfile = await this.configManager.getMyprofile();
      this.filteredUserIds = myProfile.managed_user_ids;
      this.filteredGroupIds = myProfile.managed_group_ids;
    }

    // Initialize table data source
    this.dataSourceTable = new DynamicFilterSource(this.getDataSourceCampaign<any>(), this.filterFuncCampaign);
    this.initializeTableConfigCampaign();

    this.dataSourceGroup = new DynamicFilterSource(this.getDataSourceGroup<any>(), this.filterFuncGroup);
    this.initializeTableConfigGroup();

    this.refreshList();

      
  }

  ngOnDestroy(): void {
    this.clearIntervalGetActivecampaign();
  }

  initializeTableConfigCampaign(): void {
    this.tableConfigCampaign = {
      scrollable: true,
      rowHeight: 48,
      multiSelect: false,
      enableFilter: true,
      enableExportCsv: false,
      columns: [
        {
          name: 'name',
          label: 'campaign.name',
          searchable: true,
          sortable: true
        },
        {
          name: 'processed_percent',
          label: 'active_outbound_campaign.content.progress',
          searchable: false,
          sortable: true,
          align: 'center',
          width: '100px'
        },
        {
          name: 'total_calls',
          label: 'active_outbound_campaign.content.calls',
          searchable: false,
          sortable: true,
          align: 'center',
          width: '100px'
        },
        {
          name: 'result_rate',
          label: 'active_outbound_campaign.content.success_rate',
          searchable: false,
          align: 'center',
          width: '100px'
        },
        {
          name: 'remaining_calls',
          label: 'active_outbound_campaign.content.remaining_calls',
          searchable: false,
          sortable: true,
          align: 'center',
          width: '100px'
        },
        {
          name: 'connected_agents',
          label: 'active_outbound_campaign.content.agents',
          searchable: false,
          sortable: true,
          align: 'center',
          width: '100px'
        }
      ]
    };
  }

  initializeTableConfigGroup(): void {
    this.tableConfigGroup = _.cloneDeep(this.tableConfigCampaign);
    this.tableConfigGroup.columns = this.tableConfigGroup.columns.map(col => {
      if (col.name === 'name') {
        return {
          ...col,
          label: 'group.content.name',
          searchable: true,
          sortable: true
        };
      }
      return col;
    });
  }

  private readonly filterFuncCampaign = (item: CampaignActiveInfoDisplayed): boolean => {
    const filterString = this.dataSourceTable.filter;
    return filterString ? regexSearch(item.name, filterString) : true;
  }

  private readonly filterFuncGroup = (item: GroupActiveInfoDisplayed): boolean => {
    const filterString = this.dataSourceGroup.filter;
    return filterString ? regexSearch(item.name, filterString) : true;
  }

  private setIntervalGetActivecampaign(): void {
    this.updateActiveCamapignInterval = setInterval(() => {
      this.getActiveCampaignInfo();
    }, 120 * 1000);
  }

  private clearIntervalGetActivecampaign(): void {
    if (this.updateActiveCamapignInterval) {
      clearInterval(this.updateActiveCamapignInterval);
      this.updateActiveCamapignInterval = undefined;
    }
  }

  applyFilter(): void {
    if (this.view === 'group') {
      this.filterGroups();
    } else if (this.view === 'table') {
      this.filterCampaigns();
    }
  }

  filterCampaigns(): void {
    const displayedCampaigns = this.activeCampaigns
      .filter((x: CampaignActiveInfoDisplayed) => {
        return this.filterCampaign(x);
      })
      .sort(this.sortList);

    this._dataSourceCampaign.next(displayedCampaigns);
  }

  filterGroups(): void {
    const displayedCampaigns = this.activeCampaigns
      .filter((x: CampaignActiveInfoDisplayed) => {
        return this.filterCampaign(x);
      })
      .sort(this.sortList);

    // Group campaigns by their group_id (null/undefined sẽ thành 'ungrouped')
    const groupedCampaigns = _.groupBy(displayedCampaigns, item => item.campaign.group?.id || 'ungrouped');

    const displayedGroups = [];

    // Xử lý nhóm "Ungrouped" trước
    if (groupedCampaigns.ungrouped) {
      const ungroupedCampaigns = groupedCampaigns.ungrouped;
      const aggregatedUngrouped: GroupActiveInfoDisplayed = {
        id: -1, // ID đặc biệt cho nhóm Ungrouped
        name: _ti('public.ungrouped'),
        processed_calls: _.sumBy(ungroupedCampaigns, 'processed_calls'),
        success_calls: _.sumBy(ungroupedCampaigns, 'success_calls'),
        failure_calls: _.sumBy(ungroupedCampaigns, 'failure_calls'),
        success_today_calls: _.sumBy(ungroupedCampaigns, 'success_today_calls'),
        failure_today_calls: _.sumBy(ungroupedCampaigns, 'failure_today_calls'),
        total_calls: _.sumBy(ungroupedCampaigns, 'total_calls'),
        remaining_calls: _.sumBy(ungroupedCampaigns, 'remaining_calls'),
        connected_agents: _.sumBy(ungroupedCampaigns, 'connected_agents'),
        main_agents: _.sumBy(ungroupedCampaigns, 'main_agents'),
        processed_percent: this.calculateAverageProcessedPercent(ungroupedCampaigns),
        is_group: true
      };
      displayedGroups.push(aggregatedUngrouped);
    }

    // Xử lý các nhóm khác
    for (const [groupId, groupCampaigns] of Object.entries(groupedCampaigns)) {
      if (groupId === 'ungrouped') {
        continue; // Đã xử lý ở trên
      }

      const group = groupCampaigns[0].campaign.group;
      const aggregated: GroupActiveInfoDisplayed = {
        id: group.id,
        name: group.name,
        processed_calls: _.sumBy(groupCampaigns, 'processed_calls'),
        success_calls: _.sumBy(groupCampaigns, 'success_calls'),
        failure_calls: _.sumBy(groupCampaigns, 'failure_calls'),
        success_today_calls: _.sumBy(groupCampaigns, 'success_today_calls'),
        failure_today_calls: _.sumBy(groupCampaigns, 'failure_today_calls'),
        total_calls: _.sumBy(groupCampaigns, 'total_calls'),
        remaining_calls: _.sumBy(groupCampaigns, 'remaining_calls'),
        connected_agents: _.sumBy(groupCampaigns, 'connected_agents'),
        main_agents: _.sumBy(groupCampaigns, 'main_agents'),
        processed_percent: this.calculateAverageProcessedPercent(groupCampaigns),
        is_group: true
      };
      displayedGroups.push(aggregated);
    }

    // Áp dụng sorting
    const sortedGroups = displayedGroups.sort(this.sortList);
    this._dataSourceGroup.next(sortedGroups);
  }

  private calculateAverageProcessedPercent(campaigns: CampaignActiveInfoDisplayed[]): number {
    const total = campaigns.reduce((sum, campaign) => sum + (campaign.processed_percent || 0), 0);
    return campaigns.length > 0 ? Math.round(total / campaigns.length) : 0;
  }

  async refreshList(): Promise<void> {
    this.activeCampaigns = [];
    await this.getActiveCampaignInfo();

    this.clearIntervalGetActivecampaign();
    this.setIntervalGetActivecampaign();
  }

  async getActiveCampaignInfo(): Promise<any> {
    if (_.isEmpty(this.activeCampaigns)) {
      this.activeCampaigns = [];
    }
    this.activeCampaigns.length = 0;
    try {
      const activeCampaigns: CampaignActiveInfo[] = await this.outcallService.getActiveInfo();
      this.activeCampaigns = [];

      // Update queue for activeCampaigns list
      for (const active_campaign of activeCampaigns) {
        const campaign = this.campaigns[active_campaign.id];
        const c: CampaignActiveInfoDisplayed = {
          ...active_campaign,
          campaign,
          queue: campaign.queue,
          processed_percent: active_campaign.total_calls
            ? Math.ceil((active_campaign.processed_calls * 100) / active_campaign.total_calls)
            : 0
        };

        this.activeCampaigns.push(c);
      }

      // Applying filter
      this.applyFilter();
    } catch (e) {
      this.loggerService.error('getActiveCampaignInfo', e);
    }
  }

  updateQueueAttrs(queue: CallQueueEntity, field_list: string[]): void {
    try {
      const repo = this.em.getRepository('CallQueueRepository') as CallQueueRepository;
      repo.saveAttrs(queue, field_list);
      this.toast.showSuccess(_ti('public.message.update_success'));
    } catch (e) {
      this.loggerService.error('updateQueueAttrs', e);
      if (e.error && e.error.message) {
        this.toast.showError(e.error.message);
      }
    }
  }

  private filterCampaign(activeInfo: CampaignActiveInfoDisplayed): boolean {
    const item = activeInfo.campaign;

    // Filter by filtering cretica
    if (
      (this.filters.remainingCalls && !activeInfo.remaining_calls) ||
      (this.filters.callsToday && !activeInfo.success_today_calls && !activeInfo.failure_today_calls) ||
      (this.filters.noAssignedAgent && activeInfo.main_agents) ||
      (this.filters.noConnectedAgent && activeInfo.connected_agents)
    ) {
      return false;
    }

    // Filter by group
    if (this.filters.selectedGroups.length > 0) {
      if (
        !item.group ||
        (item.group &&
          !_.includes(
            this.filters.selectedGroups,
            item.group.id
          ))
      ) {
        return false;
      }
    }

    if (this.filters.selectedAgents.length > 0) {
      const userRepo = this.em.getRepository('UserRepository') as UserRepository;
      if (!item.queue || item.queue && _.isEmpty(this.filters.selectedAgents.filter((x: number) => {
        const user = userRepo.getObjectById(x);
        return user && user.agent && item.queue.containMainAgentId(user.agent.id) || false;
      }))) {
        return false;
      }
    }

    // Filter by name
    const filterValue: string = (this.search || '').trim();
    if (!filterValue) {
      return true;
    }

    const searchTextList = [item.name, item.alias];

    for (const searchText of searchTextList) {
      if (searchText && regexSearch(searchText, filterValue)) {
        return true;
      }
    }

    return false;
  }

  sortList = (item1, item2): number => {
    const n: number = this.orderDir ? 1 : -1;
    const nn: number = n * -1;
    if (this.orderBy === 'name') {
      return item1.name > item2.name ? n : nn;
    }
    if (this.orderBy === 'connected_agents') {
      return item1.connected_agents > item2.connected_agents ? n : nn;
    }
    return 0;
  }

  changeSort($event): void {
    this.applyFilter();
  }

  changeDirection($event): void {
    this.orderDir = !this.orderDir;
    this.applyFilter();
  }

  clearGroupValue(event: MouseEvent): void {
    event.stopPropagation();
    this.filters.selectedGroups = [];
    this.applyFilter();
  }

  clearAgentValue(event: MouseEvent): void {
    event.stopPropagation();
    this.filters.selectedAgents = [];
    this.applyFilter();
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.tabSelectedIndex = event.index;
    switch (event.index) {
      case 0:
        this.view = 'table';
        break;
      case 1:
        this.view = 'group';
        break;
      default:
        console.error('No tab index');
        return;
    }

    this.applyFilter();
  }

  gotoPageCampaign($event: MouseEvent, campaignId: number): void {
    $event.stopPropagation();

    this.router.navigate(['out-call-campaigns', campaignId]);
  }

  gotoPageGroup($event: MouseEvent, groupId: number): void {
    $event.stopPropagation();

    this.router.navigate(['groups', groupId]);
  }
}
